import { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { api } from '../utils/api';
import { formatCurrency } from '../utils/format';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { TrendingUp, DollarSign, CreditCard, Banknote, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function Dashboard() {
  const { currentUser } = useApp();
  const [loading, setLoading] = useState(true);
  const [bugunData, setBugunData] = useState([]);
  const [aylikData, setAylikData] = useState([]);
  const [bankaData, setBankaData] = useState([]);
  const [taksitler, setTaksitler] = useState([]);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const [bugunRes, aylikRes, bankaRes, taksitRes] = await Promise.all([
        api.getBugunDashboard(),
        api.getAylikDashboard(),
        api.getBankaDashboard(),
        api.getVadesiYaklasanTaksitler()
      ]);

      setBugunData(bugunRes.data);
      setAylikData(aylikRes.data);
      setBankaData(bankaRes.data);
      setTaksitler(taksitRes.data);
    } catch (error) {
      console.error('Error loading dashboard:', error);
      toast.error('Dashboard verileri yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleOdeTaksit = async (taksitId) => {
    try {
      await api.odeTaksit(taksitId);
      toast.success('Taksit ödeme kaydı yapıldı');
      loadDashboardData();
    } catch (error) {
      console.error('Error paying installment:', error);
      toast.error('Taksit ödeme kaydı yapılırken hata oluştu');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-textSecondary">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  const COLORS = ['#EA580C', '#FDBA74', '#F97316', '#C2410C', '#7C2D12'];

  return (
    <div className="space-y-4 md:space-y-8" data-testid="dashboard">
      <div>
        <h1 className="text-xl md:text-3xl font-heading font-bold text-textPrimary">Dashboard</h1>
        <p className="text-xs md:text-base text-textSecondary mt-1 md:mt-2">Genel bakış ve önemli metrikler</p>
      </div>

      {/* Bugünün Özeti */}
      <section>
        <h2 className="text-lg md:text-xl font-heading font-bold text-textPrimary mb-3 md:mb-4">Bugün</h2>
        {bugunData.length === 0 ? (
          <div className="bg-surface border border-border p-8 text-center">
            <p className="text-textSecondary">Bugün henüz kayıt girilmemiş</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border border border-border">
            {bugunData.map((sube, index) => (
              <div key={index} className="bg-surface p-4 md:p-6" data-testid={`bugun-sube-${index}`}>
                <h3 className="text-base md:text-lg font-heading font-bold text-textPrimary mb-3 md:mb-4">{sube.subeAd}</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-textSecondary">Toplam Satış:</span>
                    <span className="font-mono font-bold text-textPrimary">{formatCurrency(sube.toplamSatis)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-textSecondary">Nakit:</span>
                    <span className="font-mono text-textPrimary">{formatCurrency(sube.nakitSatis)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-textSecondary">Kart:</span>
                    <span className="font-mono text-textPrimary">{formatCurrency(sube.kartToplam)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-textSecondary">Havale:</span>
                    <span className="font-mono text-textPrimary">{formatCurrency(sube.havaleToplam)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-textSecondary">Veresiye:</span>
                    <span className="font-mono text-textPrimary">{formatCurrency(sube.veresiyeSatis)}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-border">
                    <span className="text-textSecondary">Gider:</span>
                    <span className="font-mono text-error">{formatCurrency(sube.nakitGiderToplam)}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-border">
                    <span className="font-bold text-textPrimary">NET:</span>
                    <span className="font-mono font-bold text-primary text-lg">{formatCurrency(sube.net)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Bu Ay */}
      <section>
        <h2 className="text-lg md:text-xl font-heading font-bold text-textPrimary mb-3 md:mb-4">Bu Ay</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
          {/* Tablo */}
          <div className="bg-surface border border-border p-4 md:p-6">
            <h3 className="text-base md:text-lg font-heading font-bold text-textPrimary mb-3 md:mb-4">Şube Karşılaştırması</h3>
            <div className="space-y-3">
              {aylikData.map((sube, index) => (
                <div key={index} className="pb-3 border-b border-border last:border-0" data-testid={`aylik-sube-${index}`}>
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-heading font-bold text-textPrimary">{sube.subeAd}</span>
                    <span className="font-mono font-bold text-primary">{formatCurrency(sube.toplamSatis)}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div>
                      <span className="text-textSecondary block">Nakit</span>
                      <span className="font-mono text-textPrimary">{formatCurrency(sube.nakitSatis)}</span>
                    </div>
                    <div>
                      <span className="text-textSecondary block">Kart</span>
                      <span className="font-mono text-textPrimary">{formatCurrency(sube.kartToplam)}</span>
                    </div>
                    <div>
                      <span className="text-textSecondary block">Havale</span>
                      <span className="font-mono text-textPrimary">{formatCurrency(sube.havaleToplam)}</span>
                    </div>
                  </div>
                  <div className="mt-2 flex justify-between text-xs">
                    <span className="text-textSecondary">NET:</span>
                    <span className="font-mono font-bold text-success">{formatCurrency(sube.net)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Grafik */}
          <div className="bg-surface border border-border p-4 md:p-6">
            <h3 className="text-base md:text-lg font-heading font-bold text-textPrimary mb-3 md:mb-4">Şube Satışları</h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={aylikData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E7E5E4" />
                <XAxis dataKey="subeAd" tick={{ fontSize: 12, fill: '#57534E' }} />
                <YAxis tick={{ fontSize: 12, fill: '#57534E' }} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#FFFFFF', border: '1px solid #E7E5E4' }}
                  formatter={(value) => formatCurrency(value)}
                />
                <Bar dataKey="toplamSatis" fill="#EA580C" name="Toplam Satış" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      {/* Banka Bazlı */}
      <section>
        <h2 className="text-lg md:text-xl font-heading font-bold text-textPrimary mb-3 md:mb-4">Banka Bazlı Ödemeler (Bu Ay)</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
          {/* Tablo */}
          <div className="bg-surface border border-border p-4 md:p-6">
            <div className="space-y-3">
              {bankaData.slice(0, 10).map((banka, index) => (
                <div key={index} className="pb-3 border-b border-border last:border-0" data-testid={`banka-${index}`}>
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-heading font-bold text-textPrimary">{banka.ad}</span>
                    <span className="font-mono font-bold text-primary">{formatCurrency(banka.toplam)}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="text-textSecondary block">Kart POS</span>
                      <span className="font-mono text-textPrimary">{formatCurrency(banka.kartPos)}</span>
                    </div>
                    <div>
                      <span className="text-textSecondary block">Havale</span>
                      <span className="font-mono text-textPrimary">{formatCurrency(banka.havale)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Grafik */}
          <div className="bg-surface border border-border p-4 md:p-6">
            <h3 className="text-base md:text-lg font-heading font-bold text-textPrimary mb-3 md:mb-4">Dağılım</h3>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={bankaData.slice(0, 5)}
                  dataKey="toplam"
                  nameKey="ad"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label={(entry) => entry.ad}
                >
                  {bankaData.slice(0, 5).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => formatCurrency(value)} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      {/* Vadesi Yaklaşan Taksitler */}
      <section>
        <h2 className="text-lg md:text-xl font-heading font-bold text-textPrimary mb-3 md:mb-4 flex items-center flex-wrap gap-2">
          <AlertCircle className="h-5 w-5 md:h-6 md:w-6 text-error" />
          <span>Vadesi Yaklaşan Taksitler (30 Gün)</span>
        </h2>
        {taksitler.length === 0 ? (
          <div className="bg-surface border border-border p-8 text-center">
            <p className="text-textSecondary">Vadesi yaklaşan taksit yok</p>
          </div>
        ) : (
          <div className="bg-surface border border-border">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="taksitler-table">
                <thead className="bg-surfaceSubtle border-b border-border">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">Vade Tarihi</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">Tutar</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">Durum</th>
                    {currentUser?.rol === 'admin' && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">İşlem</th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {taksitler.map((taksit, index) => (
                    <tr key={taksit.id} data-testid={`taksit-row-${index}`}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-textPrimary">{taksit.vadeTarihi}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-mono font-bold text-textPrimary">{formatCurrency(taksit.tutar)}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 text-xs font-medium bg-error text-white">
                          Ödenmedi
                        </span>
                      </td>
                      {currentUser?.rol === 'admin' && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <button
                            onClick={() => handleOdeTaksit(taksit.id)}
                            data-testid={`ode-taksit-btn-${index}`}
                            className="text-primary hover:text-primaryHover font-medium"
                          >
                            Ödendi Olarak İşaretile
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}